import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { UsagesResp } from '@/api/types';
import { parseCollectionAllocation } from '@/lib/resource-helpers';

interface UsagesListProps {
  resourceName: string;
}

export default function UsagesList({ resourceName }: UsagesListProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['usages', resourceName],
    queryFn: () =>
      api<UsagesResp>(
        `/v1/usages?resource-id=${encodeURIComponent(resourceName)}`,
      ),
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });

  if (isLoading) {
    return (
      <div className="rounded-md border bg-card px-3 py-6 text-center text-xs text-muted-foreground">
        Loading current allocations…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
        Failed to load usages: {error instanceof Error ? error.message : String(error)}
      </div>
    );
  }

  const usages = data?.usages ?? [];

  if (usages.length === 0) {
    return (
      <div className="rounded-md border bg-card px-3 py-6 text-center text-xs text-muted-foreground">
        No current allocations.
      </div>
    );
  }

  return (
    <div className="rounded-md border bg-card overflow-hidden">
      <div className="max-h-64 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 sticky top-0">
            <tr className="text-left">
              <th className="px-3 py-1.5 font-medium">User</th>
              <th className="px-3 py-1.5 font-medium">Amount</th>
              <th className="px-3 py-1.5 font-medium">Project</th>
              <th className="px-3 py-1.5 font-medium">Department</th>
              <th className="px-3 py-1.5 font-medium">Allocated</th>
            </tr>
          </thead>
          <tbody>
            {usages.map((u, i) => {
              // Collection allocations carry which item indexes they hold in
              // stringList["0"] (base64 JSON); StringList folds its items into
              // `amount` server-side and other types have none — so the index
              // suffix is Collection-only. See parseCollectionAllocation.
              const collectionItems =
                u.resourceType === 'Collection'
                  ? parseCollectionAllocation(u.stringList?.[0])
                  : null;
              return (
              // break-all (word-break: break-all) on every text cell.
              // The earlier attempt used break-words (overflow-wrap:
              // break-word), which only allows breaking *when* the
              // content would otherwise overflow — it does **not**
              // reduce the cell's CSS min-content. So a long cluster
              // ID / username / project name still gave this nested
              // <table>'s columns a high preferred min-content width,
              // which propagated through the wrapping div into the
              // outer <td colSpan={5}> on ResourcesPage, and the
              // outer auto-layout redistributed that width across
              // cols 2-6 of the resources table — visible as the
              // outer columns shrinking / shifting when a row was
              // expanded.
              // break-all forces a break point at every character,
              // dropping min-content to 1ch and keeping the nested
              // table's preferred width bounded by its container.
              <tr key={`${u.user}-${u.allocationDate}-${i}`} className="border-t">
                <td className="px-3 py-1.5 font-medium break-all">{u.user || '—'}</td>
                <td className="px-3 py-1.5 font-mono break-all">
                  {u.amount || '—'}
                  {collectionItems && collectionItems.length > 0 && (
                    <span
                      className="ml-1 text-muted-foreground"
                      title={collectionItems
                        .map((it) => `[${it.index}] ${it.value}`)
                        .join('\n')}
                    >
                      [{collectionItems.map((it) => it.index).join(', ')}]
                    </span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-muted-foreground break-all">
                  {u.project || '—'}
                </td>
                <td className="px-3 py-1.5 text-muted-foreground break-all">
                  {u.department || '—'}
                </td>
                <td className="px-3 py-1.5 text-muted-foreground font-mono">
                  {formatDate(u.allocationDate)}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="border-t bg-muted/20 px-3 py-1 text-[11px] text-muted-foreground">
        {usages.length} allocation{usages.length === 1 ? '' : 's'}
      </div>
    </div>
  );
}

function formatDate(s: string): string {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
